// /home/vitruvio/puntum-frontend/src/pages/Login.jsx

import React, { useState, useEffect } from 'react';

const API_BASE_URL = "https://api-dev.puntum.cl";
const GOOGLE_CLIENT_ID = "327524728961-220h1i5adccrqb1v7c5rhpp6tfr130vr.apps.googleusercontent.com";

export default function Login({ onLoginSuccess }) {
  // Modos disponibles: 'login' | 'register' | 'otp' | 'forgot' | 'reset' | 'setup_password'
  const [mode, setMode] = useState('login'); 
  
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    password: '',
    confirmPassword: '',
    otp: ''
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ⏱️ Temporizador para Reenvío de Código OTP
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // 🌡️ Evaluador de Fuerza de Contraseña (Termómetro)
  const getPasswordStrength = (pass) => {
    if (!pass) return { score: 0, label: '', color: '#e2e8f0' };
    let score = 0;
    if (pass.length >= 6) score += 1;
    if (pass.length >= 10) score += 1;
    if (/[A-Z]/.test(pass)) score += 1;
    if (/[0-9]/.test(pass)) score += 1;
    if (/[^A-Za-z0-9]/.test(pass)) score += 1;

    if (score <= 2) return { score: 33, label: 'Débil ⚠️', color: '#ef4444' };
    if (score <= 4) return { score: 66, label: 'Media 🟡', color: '#f59e0b' };
    return { score: 100, label: 'Fuerte 🛡️', color: '#10b981' };
  };

  const strength = getPasswordStrength(formData.password);

  // 🌐 SDK Google Identity Services
  useEffect(() => {
    if (mode !== 'login' && mode !== 'register') return;

    const handleCredentialResponse = async (response) => {
      setLoading(true);
      setErrorMsg('');

      try {
        const res = await fetch(`${API_BASE_URL}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: response.credential })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Error al autenticar con Google');

        if (data.access_token) localStorage.setItem('puntum_token', data.access_token);
        if (data.user) localStorage.setItem('puntum_user', JSON.stringify(data.user));

        // 🛡️ Si requiere configurar clave por ser usuario nuevo de Google
        if (data.needs_password_setup) {
          setFormData(prev => ({ ...prev, email: data.user.email }));
          setSuccessMsg('¡Autenticación exitosa! Para mayor seguridad, define una contraseña para tu cuenta.');
          setMode('setup_password');
          return;
        }

        setSuccessMsg('¡Autenticación con Google exitosa!');
        setTimeout(() => {
          if (onLoginSuccess) onLoginSuccess(data.user);
          else window.location.reload();
        }, 800);

      } catch (err) {
        setErrorMsg(err.message || 'Falló el inicio de sesión con Google');
      } finally {
        setLoading(false);
      }
    };

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse
        });

        const btnContainer = document.getElementById('googleBtnContainer');
        if (btnContainer) {
          window.google.accounts.id.renderButton(btnContainer, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: mode === 'register' ? 'signup_with' : 'continue_with',
            shape: 'rectangular'
          });
        }
      }
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [mode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errorMsg) setErrorMsg('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setErrorMsg('');
    setSuccessMsg('');
    setFormData(prev => ({ ...prev, password: '', confirmPassword: '', otp: '' }));
  };

  // 1️⃣ INICIAR SESIÓN TRADICIONAL
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Credenciales inválidas');

      if (data.access_token) localStorage.setItem('puntum_token', data.access_token);
      
      const userObj = data.user || {
        nombre: formData.nombre || 'Usuario',
        email: formData.email,
        empresa: formData.empresa || 'Mi PyME'
      };
      localStorage.setItem('puntum_user', JSON.stringify(userObj));

      if (onLoginSuccess) onLoginSuccess(userObj);

    } catch (err) {
      setErrorMsg(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // 2️⃣ REGISTRO TRADICIONAL
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formData.nombre,
          empresa: formData.empresa,
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo procesar el registro');

      setSuccessMsg(`Código enviado a ${formData.email}. Revisa tu bandeja de entrada.`);
      setResendCooldown(60);
      setMode('otp');

    } catch (err) {
      setErrorMsg(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // 3️⃣ CÓDIGO OTP (REGISTRO)
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    if (formData.otp.trim().length !== 6) {
      setErrorMsg('El código debe contener exactamente 6 dígitos');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp.trim()
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Código de verificación inválido o expirado');

      if (data.access_token) localStorage.setItem('puntum_token', data.access_token);

      const userObj = data.user || {
        nombre: formData.nombre,
        empresa: formData.empresa,
        email: formData.email
      };
      localStorage.setItem('puntum_user', JSON.stringify(userObj));

      setSuccessMsg('¡Cuenta activada con éxito!');
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess(userObj);
        else window.location.reload();
      }, 1000);

    } catch (err) {
      setErrorMsg(err.message || 'Error al validar el código');
    } finally {
      setLoading(false);
    }
  };

  // 4️⃣ SOLICITAR / REENVIAR CÓDIGO DE RECUPERACIÓN DE CONTRASEÑA
  const handleForgotPassword = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error al solicitar el código');

      setSuccessMsg(`Si el correo está registrado, enviamos un código a ${formData.email}.`);
      setResendCooldown(60);
      setMode('reset');

    } catch (err) {
      setErrorMsg(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // 5️⃣ RESTABLECER CONTRASEÑA
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    if (formData.otp.trim().length !== 6) {
      setErrorMsg('El código debe contener exactamente 6 dígitos');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp.trim(),
          new_password: formData.password
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Código inválido o expirado');

      setSuccessMsg('¡Contraseña actualizada con éxito! Ya puedes ingresar.');
      setTimeout(() => {
        switchMode('login');
      }, 1500);

    } catch (err) {
      setErrorMsg(err.message || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  // 6️⃣ GUARDAR CONTRASEÑA INICIAL (POST GOOGLE LOGIN)
  const handleSetupPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setErrorMsg('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const rawUser = localStorage.getItem('puntum_user');
      const userObj = rawUser ? JSON.parse(rawUser) : {};
      userObj.needs_password_setup = false;

      localStorage.setItem('puntum_user', JSON.stringify(userObj));

      setSuccessMsg('¡Contraseña configurada con éxito!');
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess(userObj);
        else window.location.reload();
      }, 800);

    } catch (err) {
      setErrorMsg('Error al guardar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  // 🎨 Bloque Reutilizable: Doble Contraseña + Termómetro
  const renderPasswordFields = () => (
    <>
      <div className="form-group">
        <label className="form-label">Contraseña</label>
        <input 
          type="password" 
          name="password" 
          required 
          minLength={6} 
          value={formData.password} 
          onChange={handleChange} 
          className="form-input" 
          placeholder="Mínimo 6 caracteres" 
        />
        
        {formData.password && (
          <div style={{ marginTop: '0.4rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
              <span>Seguridad de la clave:</span>
              <span style={{ fontWeight: 'bold', color: strength.color }}>{strength.label}</span>
            </div>
            <div style={{ width: '100%', height: '5px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${strength.score}%`, height: '100%', backgroundColor: strength.color, transition: 'all 0.3s' }}></div>
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Confirmar Contraseña</label>
        <input 
          type="password" 
          name="confirmPassword" 
          required 
          minLength={6} 
          value={formData.confirmPassword} 
          onChange={handleChange} 
          className="form-input" 
          placeholder="Repite tu contraseña" 
        />
      </div>
    </>
  );

  return (
    <div className="login-container">
      <div className="login-card">
        
        {/* Encabezado */}
        <div className="login-header">
          <div className="login-logo">📦 Puntum POS</div>
          <h2 className="login-title">
            {mode === 'login' && 'Ingresar a tu cuenta'}
            {mode === 'register' && 'Crear cuenta en Puntum POS'}
            {mode === 'otp' && 'Verifica tu Correo'}
            {mode === 'forgot' && 'Recuperar Contraseña'}
            {mode === 'reset' && 'Nueva Contraseña'}
            {mode === 'setup_password' && 'Configura tu Contraseña'}
          </h2>
          <p className="login-subtitle">
            {mode === 'login' && 'Sistema de Gestión e Inventario PyME'}
            {mode === 'register' && 'Comienza tu prueba o activa tu plan'}
            {mode === 'otp' && 'Ingresa el código de 6 dígitos enviado por email'}
            {mode === 'forgot' && 'Ingresa tu correo para recibir un código de recuperación'}
            {mode === 'reset' && 'Ingresa el código de 6 dígitos enviado y define tu clave'}
            {mode === 'setup_password' && 'Crea una contraseña para acceder también sin usar Google'}
          </p>
        </div>

        {/* Alertas */}
        {errorMsg && (
          <div style={{ padding: '0.75rem', backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div style={{ padding: '0.75rem', backgroundColor: '#ecfdf5', borderLeft: '4px solid #10b981', color: '#065f46', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
            {successMsg}
          </div>
        )}

        {/* VISTA 1: LOGIN */}
        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Correo Electrónico</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="form-input" placeholder="usuario@tuempresa.cl" />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="form-label">Contraseña</label>
                <button type="button" onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: 'var(--primary, #2563eb)', fontSize: '0.75rem', cursor: 'pointer', fontWeight: '500', padding: 0 }}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <input type="password" name="password" required value={formData.password} onChange={handleChange} className="form-input" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </button>
            <div className="text-center mt-2">
              <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>¿No tienes una cuenta? </span>
              <button type="button" onClick={() => switchMode('register')} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}>
                Regístrate Gratis
              </button>
            </div>
          </form>
        )}

        {/* VISTA 2: REGISTRO */}
        {mode === 'register' && (
          <form onSubmit={handleRegisterSubmit}>
            <div className="form-group">
              <label className="form-label">Nombre Completo</label>
              <input type="text" name="nombre" required value={formData.nombre} onChange={handleChange} className="form-input" placeholder="Ej: Juan Pérez" />
            </div>
            <div className="form-group">
              <label className="form-label">Nombre de la Empresa</label>
              <input type="text" name="empresa" required value={formData.empresa} onChange={handleChange} className="form-input" placeholder="Ej: Comercial El Sol SpA" />
            </div>
            <div className="form-group">
              <label className="form-label">Correo Electrónico</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="form-input" placeholder="contacto@tuempresa.cl" />
            </div>

            {renderPasswordFields()}

            <button type="submit" disabled={loading} className="btn btn-primary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Enviando código...' : 'Crear Cuenta y Recibir Código'}
            </button>
            <div className="text-center mt-2">
              <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>¿Ya tienes cuenta? </span>
              <button type="button" onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}>
                Iniciar Sesión
              </button>
            </div>
          </form>
        )}

        {/* BOTÓN GOOGLE */}
        {(mode === 'login' || mode === 'register') && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', margin: '1.25rem 0 0.75rem 0', color: 'var(--text-light, #64748b)', fontSize: '0.8rem' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e2e8f0)' }}></div>
              <span style={{ padding: '0 0.5rem' }}>o continúa con</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e2e8f0)' }}></div>
            </div>

            <div id="googleBtnContainer" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}></div>
          </>
        )}

        {/* VISTA 3: OTP REGISTRO */}
        {mode === 'otp' && (
          <form onSubmit={handleVerifyOTP}>
            <div className="form-group">
              <label className="form-label text-center">Código de Confirmación</label>
              <input type="text" name="otp" required maxLength={6} value={formData.otp} onChange={handleChange} className="form-input text-center" style={{ fontSize: '1.5rem', letterSpacing: '0.25rem', fontWeight: 'bold' }} placeholder="123456" />
            </div>
            <button type="submit" disabled={loading} className="btn btn-secondary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Verificando...' : 'Activar Cuenta'}
            </button>
            <div className="flex-between mt-2" style={{ fontSize: '0.875rem' }}>
              <button type="button" onClick={() => switchMode('register')} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer' }}>
                ← Cambiar datos
              </button>
              
              <button 
                type="button" 
                onClick={handleRegisterSubmit} 
                disabled={resendCooldown > 0 || loading}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: resendCooldown > 0 ? '#94a3b8' : 'var(--primary)', 
                  fontWeight: '600', 
                  cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' 
                }}
              >
                {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código'}
              </button>
            </div>
          </form>
        )}

        {/* VISTA 4: RECUPERAR CONTRASEÑA */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <label className="form-label">Correo Electrónico Registrado</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="form-input" placeholder="usuario@tuempresa.cl" />
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Enviando...' : 'Enviar Código de Recuperación'}
            </button>
            <div className="text-center mt-2">
              <button type="button" onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '0.875rem' }}>
                ← Volver al inicio de sesión
              </button>
            </div>
          </form>
        )}

        {/* VISTA 5: RESETEAR CONTRASEÑA */}
        {mode === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="form-label">Código de Recuperación (6 dígitos)</label>

                <button 
                  type="button" 
                  onClick={() => handleForgotPassword()} 
                  disabled={resendCooldown > 0 || loading}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: resendCooldown > 0 ? '#94a3b8' : 'var(--primary, #2563eb)', 
                    fontSize: '0.75rem', 
                    cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', 
                    fontWeight: '600', 
                    padding: 0 
                  }}
                >
                  {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : '¿No llegó? Reenviar código'}
                </button>
              </div>

              <input type="text" name="otp" required maxLength={6} value={formData.otp} onChange={handleChange} className="form-input text-center" style={{ fontSize: '1.25rem', letterSpacing: '0.25rem', fontWeight: 'bold' }} placeholder="123456" />
            </div>

            {renderPasswordFields()}

            <button type="submit" disabled={loading} className="btn btn-primary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Actualizando...' : 'Cambiar y Guardar Contraseña'}
            </button>
            
            <div className="text-center mt-2">
              <button type="button" onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '0.875rem' }}>
                ← Cancelar
              </button>
            </div>
          </form>
        )}

        {/* VISTA 6: CONFIGURAR CLAVE OBLIGATORIA GOOGLE */}
        {mode === 'setup_password' && (
          <form onSubmit={handleSetupPassword}>
            {renderPasswordFields()}

            <button type="submit" disabled={loading} className="btn btn-primary btn-full" style={{ marginTop: '0.5rem' }}>
              {loading ? 'Guardando...' : 'Completar Acceso al Sistema'}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}